import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { useOptimizedUserRole } from './useOptimizedUserRole';
import { toast } from '@/hooks/use-toast';

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'success' | 'error';
  category: 'system' | 'client' | 'payment' | 'connection' | 'case_update' | 'admin_alert';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  is_read: boolean;
  created_at: string;
  action_url?: string;
  action_label?: string;
  metadata?: Record<string, any>;
}

export interface NotificationPreferences {
  email_notifications: boolean;
  push_notifications: boolean;
  sms_notifications: boolean;
  case_updates: boolean;
  payment_reminders: boolean;
  system_updates: boolean;
  connection_alerts: boolean;
  client_messages: boolean;
  admin_alerts: boolean;
}

export const useNotifications = () => {
  const { user } = useAuth();
  const { isAdmin, isAttorney, isClient } = useOptimizedUserRole();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [preferences, setPreferences] = useState<NotificationPreferences>({
    email_notifications: true,
    push_notifications: true,
    sms_notifications: false,
    case_updates: true,
    payment_reminders: true,
    system_updates: true,
    connection_alerts: true,
    client_messages: true,
    admin_alerts: true,
  });
  const [loading, setLoading] = useState(true);

  // Fetch notifications
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('notifications')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching notifications:', error);
        } else {
          setNotifications((data || []) as Notification[]);
        }
      } catch (error) {
        console.error('Error fetching notifications:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [user]);

  // Fetch preferences
  useEffect(() => {
    const fetchPreferences = async () => {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from('notification_preferences')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error) {
          console.error('Error fetching preferences:', error);
        } else if (data) {
          // Map new column names to old interface for backward compatibility
          setPreferences({
            email_notifications: data.enabled_channels?.includes('email') ?? true,
            push_notifications: data.enabled_channels?.includes('push') ?? true,
            sms_notifications: data.enabled_channels?.includes('sms') ?? false,
            case_updates: data.notify_case_updates ?? true,
            payment_reminders: data.notify_payment_received ?? true,
            system_updates: true, // Always enabled for now
            connection_alerts: true, // Always enabled for now
            client_messages: data.notify_new_messages ?? true,
            admin_alerts: true, // Always enabled for now
          });
        }
      } catch (error) {
        console.error('Error fetching preferences:', error);
      }
    };

    fetchPreferences();
  }, [user]);

  // Set up real-time notifications listening
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(`notifications-${user.id}-${Math.random().toString(36).slice(2, 10)}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${user.id}`
      }, (payload) => {
        const newNotification = payload.new as Notification;
        setNotifications(prev => [newNotification, ...prev]);
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${user.id}`
      }, (payload) => {
        const updatedNotification = payload.new as Notification;
        setNotifications(prev => 
          prev.map(n => n.id === updatedNotification.id ? updatedNotification : n)
        );
      })
      .on('postgres_changes', {
        event: 'DELETE',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${user.id}`
      }, (payload) => {
        const deletedNotification = payload.old as Notification;
        setNotifications(prev => prev.filter(n => n.id !== deletedNotification.id));
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const markAsRead = async (id: string) => {
    try {
      // Optimistically update local state first
      setNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, is_read: true } : n)
      );

      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', id)
        .eq('user_id', user?.id);

      if (error) {
        console.error('Error marking notification as read:', error);
        toast({
          title: "Error",
          description: "Failed to mark notification as read. Please try again.",
          variant: "destructive",
        });
        // Revert optimistic update on error
        setNotifications(prev => 
          prev.map(n => n.id === id ? { ...n, is_read: false } : n)
        );
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast({
        title: "Error",
        description: "Failed to mark notification as read. Please try again.",
        variant: "destructive",
      });
      // Revert optimistic update on error
      setNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, is_read: false } : n)
      );
    }
  };

  const markAllAsRead = async () => {
    try {
      // Optimistically update local state first
      setNotifications(prev => 
        prev.map(n => ({ ...n, is_read: true }))
      );

      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', user?.id)
        .eq('is_read', false);

      if (error) {
        console.error('Error marking all notifications as read:', error);
        toast({
          title: "Error",
          description: "Failed to mark all notifications as read. Please try again.",
          variant: "destructive",
        });
        // Revert optimistic update on error - refetch to get correct state
        const { data } = await supabase
          .from('notifications')
          .select('*')
          .eq('user_id', user?.id)
          .order('created_at', { ascending: false });
        
        if (data) {
          setNotifications(data as Notification[]);
        }
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      toast({
        title: "Error",
        description: "Failed to mark all notifications as read. Please try again.",
        variant: "destructive",
      });
    }
  };

  const deleteNotification = async (id: string) => {
    try {
      // Optimistically update local state first
      setNotifications(prev => prev.filter(n => n.id !== id));

      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', id)
        .eq('user_id', user?.id);

      if (error) {
        console.error('Error deleting notification:', error);
        toast({
          title: "Error",
          description: "Failed to delete notification. Please try again.",
          variant: "destructive",
        });
        // Revert optimistic update on error - refetch to get correct state
        const { data } = await supabase
          .from('notifications')
          .select('*')
          .eq('user_id', user?.id)
          .order('created_at', { ascending: false });
        
        if (data) {
          setNotifications(data as Notification[]);
        }
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast({
        title: "Error",
        description: "Failed to delete notification. Please try again.",
        variant: "destructive",
      });
      // Revert optimistic update on error - refetch to get correct state
      const { data } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });
      
      if (data) {
        setNotifications(data as Notification[]);
      }
    }
  };

  const updatePreferences = async (newPreferences: Partial<NotificationPreferences>) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('notification_preferences')
        .upsert({
          user_id: user.id,
          ...preferences,
          ...newPreferences,
        } as any);

      if (error) {
        console.error('Error updating preferences:', error);
      } else {
        setPreferences(prev => ({ ...prev, ...newPreferences }));
      }
    } catch (error) {
      console.error('Error updating preferences:', error);
    }
  };

  const clearAll = async () => {
    if (!user) return;
    const previous = notifications;
    setNotifications([]);

    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('user_id', user.id);

      if (error) {
        console.error('Error clearing notifications:', error);
        toast({
          title: 'Error',
          description: 'Failed to clear notifications. Please try again.',
          variant: 'destructive',
        });
        setNotifications(previous);
      }
    } catch (error) {
      console.error('Error clearing notifications:', error);
      toast({
        title: 'Error',
        description: 'Failed to clear notifications. Please try again.',
        variant: 'destructive',
      });
      setNotifications(previous);
    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return {
    notifications,
    preferences,
    loading,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAll,
    updatePreferences,
  };
};